import { c as _c } from "react/compiler-runtime";
import type { CSSProperties, ReactNode } from "react";
import { clampEntriesGridLimit, type EntriesGridRenderConfig, type EntriesGridResolvedItem, effectiveEntriesGridPresentation, truncateEntriesGridText } from "../ui/lib/entriesGridBinding";
import { formatEntryMetaDate, formatReadTime } from "../ui/lib/entryMetaBinding";
import styles from "./entriesGrid.module.css";
import { BUILDER_FEATURED_IMAGE_PLACEHOLDER_SRC } from "./featuredImagePlaceholder";
import { IMAGE_PLACEHOLDER_INTRINSIC, imgElementIntrinsicProps } from "./mediaResponsive";
export type EntriesGridPublicProps = {
  items: EntriesGridResolvedItem[];
  config?: Partial<EntriesGridRenderConfig>;
  /** Effective viewport for responsiveLayout merge (public SSR usually desktop + CSS). */
  viewport?: "desktop" | "tablet" | "mobile";
  className?: string;
  style?: CSSProperties;
  /** When true, cards are non-navigating (canvas preview). */
  preview?: boolean;
  emptyMessage?: string;
  /**
   * When true (default), CSS also stacks columns on small screens even if
   * `responsiveLayout` was not authored — safety net for MCP / older saves.
   */
  autoStack?: boolean;
};
function EntryMedia(t0) {
  const $ = _c(13);
  const {
    imageUrl,
    imageAlt,
    imageWidth,
    imageHeight,
    className
  } = t0;
  let t1;
  if ($[0] !== imageUrl) {
    t1 = imageUrl?.trim() || BUILDER_FEATURED_IMAGE_PLACEHOLDER_SRC;
    $[0] = imageUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const src = t1;
  const t2 = imageWidth ?? undefined;
  const t3 = imageHeight ?? undefined;
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = imgElementIntrinsicProps(t2, t3);
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const intrinsic = t4;
  const fallback = src === BUILDER_FEATURED_IMAGE_PLACEHOLDER_SRC ? IMAGE_PLACEHOLDER_INTRINSIC : undefined;
  const t5 = intrinsic.height ?? fallback?.height;
  const t6 = intrinsic.width ?? fallback?.width;
  let t7;
  if ($[5] !== imageAlt || $[6] !== src || $[7] !== t5 || $[8] !== t6) {
    t7 = <img alt={imageAlt} height={t5} loading="lazy" src={src} width={t6} />;
    $[5] = imageAlt;
    $[6] = src;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== className || $[11] !== t7) {
    t8 = <div className={className}>{t7}</div>;
    $[10] = className;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}
function CardMeta(t0) {
  const $ = _c(27);
  const {
    entry,
    showDate,
    showAuthor,
    showTaxonomy,
    showReadTime
  } = t0;
  let t1;
  if ($[0] !== entry || $[1] !== showDate) {
    t1 = showDate ? formatEntryMetaDate(entry.publishedAt, "medium") : "";
    $[0] = entry;
    $[1] = showDate;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const dateLabel = t1;
  let t2;
  if ($[3] !== entry || $[4] !== showReadTime) {
    t2 = showReadTime ? formatReadTime(entry.readTimeMinutes, "min read") : "";
    $[3] = entry;
    $[4] = showReadTime;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const readLabel = t2;
  let nodes;
  if ($[6] !== dateLabel || $[7] !== entry || $[8] !== readLabel || $[9] !== showAuthor || $[10] !== showTaxonomy) {
    nodes = [];
    if (showTaxonomy && entry.category) {
      let t3;
      if ($[12] !== entry.category.accent) {
        t3 = {
          "--tag-accent": entry.category.accent
        };
        $[12] = entry.category.accent;
        $[13] = t3;
      } else {
        t3 = $[13];
      }
      const t4 = t3 as CSSProperties;
      let t5;
      if ($[14] !== entry.category.label || $[15] !== t4) {
        t5 = <span className={styles.tag} key="tax" style={t4}>{entry.category.label}</span>;
        $[14] = entry.category.label;
        $[15] = t4;
        $[16] = t5;
      } else {
        t5 = $[16];
      }
      nodes.push(t5);
    }
    if (showAuthor && entry.authorName?.trim()) {
      let t3;
      if ($[17] !== entry.authorName) {
        t3 = <span key="author">{entry.authorName}</span>;
        $[17] = entry.authorName;
        $[18] = t3;
      } else {
        t3 = $[18];
      }
      nodes.push(t3);
    }
    if (dateLabel) {
      let t3;
      if ($[19] !== dateLabel) {
        t3 = <span key="date">{dateLabel}</span>;
        $[19] = dateLabel;
        $[20] = t3;
      } else {
        t3 = $[20];
      }
      nodes.push(t3);
    }
    if (readLabel) {
      let t3;
      if ($[21] !== readLabel) {
        t3 = <span key="read">{readLabel}</span>;
        $[21] = readLabel;
        $[22] = t3;
      } else {
        t3 = $[22];
      }
      nodes.push(t3);
    }
    $[6] = dateLabel;
    $[7] = entry;
    $[8] = readLabel;
    $[9] = showAuthor;
    $[10] = showTaxonomy;
    $[11] = nodes;
  } else {
    nodes = $[11];
  }
  if (nodes.length === 0) {
    return null;
  }
  let t3;
  if ($[23] !== entry || $[24] !== nodes || $[25] !== showTaxonomy) {
    t3 = <div className={styles.metaRow}>{nodes.map((node, index) => <span key={index}>{index > 0 && !(showTaxonomy && index === 1 && entry.category) ? <span aria-hidden="true"> · </span> : null}{index > 0 && showTaxonomy && index === 1 && entry.category ? <span aria-hidden="true"> · </span> : null}{node}</span>)}</div>;
    $[23] = entry;
    $[24] = nodes;
    $[25] = showTaxonomy;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  return t3;
}
function Cta(t0) {
  const $ = _c(4);
  const {
    style,
    label
  } = t0;
  if (style === "none") {
    return null;
  }
  if (style === "button") {
    let t1;
    if ($[0] !== label) {
      t1 = <span className={styles.ctaButton}>{label}</span>;
      $[0] = label;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== label) {
    t1 = <span className={styles.cta}>{label} →</span>;
    $[2] = label;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
type CardOpts = ReturnType<typeof effectiveEntriesGridPresentation> & {
  preview: boolean;
};
function CardTitle(t0) {
  const $ = _c(8);
  const {
    title,
    className,
    opts
  } = t0;
  let t1;
  if ($[0] !== opts.titleMaxChars || $[1] !== opts.truncateTitle || $[2] !== title) {
    t1 = truncateEntriesGridText(title, {
      enabled: opts.truncateTitle,
      maxChars: opts.titleMaxChars
    });
    $[0] = opts.titleMaxChars;
    $[1] = opts.truncateTitle;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const clipped = t1;
  const t2 = clipped.truncated ? clipped.full : undefined;
  let t3;
  if ($[4] !== className || $[5] !== clipped.text || $[6] !== t2) {
    t3 = <span className={className} title={t2}>{clipped.text}</span>;
    $[4] = className;
    $[5] = clipped.text;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function CardExcerpt(t0) {
  const $ = _c(8);
  const {
    excerpt,
    className,
    opts
  } = t0;
  let t1;
  if ($[0] !== excerpt || $[1] !== opts.excerptMaxChars || $[2] !== opts.truncateExcerpt) {
    t1 = truncateEntriesGridText(excerpt, {
      enabled: opts.truncateExcerpt,
      maxChars: opts.excerptMaxChars
    });
    $[0] = excerpt;
    $[1] = opts.excerptMaxChars;
    $[2] = opts.truncateExcerpt;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const clipped = t1;
  const t2 = clipped.truncated ? clipped.full : undefined;
  let t3;
  if ($[4] !== className || $[5] !== clipped.text || $[6] !== t2) {
    t3 = <p className={className} title={t2}>{clipped.text}</p>;
    $[4] = className;
    $[5] = clipped.text;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function GridCard(t0) {
  const $ = _c(48);
  const {
    entry,
    opts,
    featured: t1
  } = t0;
  const featured = t1 === undefined ? false : t1;
  const overlay = opts.cardStyle === "overlay" && !featured;
  const mediaClass = featured ? styles.featuredMedia : styles.cardMedia;
  const titleClass = featured ? styles.featuredTitle : styles.cardTitle;
  const excerptClass = featured ? styles.featuredExcerpt : styles.cardExcerpt;
  let t2;
  if ($[0] !== entry || $[1] !== mediaClass || $[2] !== opts.showImage) {
    t2 = opts.showImage ? <EntryMedia className={mediaClass} imageAlt={entry.imageAlt || entry.title} imageHeight={entry.imageHeight} imageUrl={entry.imageUrl} imageWidth={entry.imageWidth} /> : null;
    $[0] = entry;
    $[1] = mediaClass;
    $[2] = opts.showImage;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = featured ? styles.featuredBody : styles.cardBody;
  let t4;
  if ($[4] !== entry || $[5] !== opts.showAuthor || $[6] !== opts.showDate || $[7] !== opts.showReadTime || $[8] !== opts.showTaxonomy) {
    t4 = <CardMeta entry={entry} showAuthor={opts.showAuthor} showDate={opts.showDate} showReadTime={opts.showReadTime} showTaxonomy={opts.showTaxonomy} />;
    $[4] = entry;
    $[5] = opts.showAuthor;
    $[6] = opts.showDate;
    $[7] = opts.showReadTime;
    $[8] = opts.showTaxonomy;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== entry || $[11] !== opts || $[12] !== titleClass) {
    t5 = opts.showTitle ? <CardTitle className={titleClass} opts={opts} title={entry.title} /> : null;
    $[10] = entry;
    $[11] = opts;
    $[12] = titleClass;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== entry || $[15] !== excerptClass || $[16] !== opts) {
    t6 = opts.showExcerpt && entry.excerpt?.trim() ? <CardExcerpt className={excerptClass} excerpt={entry.excerpt} opts={opts} /> : null;
    $[14] = entry;
    $[15] = excerptClass;
    $[16] = opts;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== opts.ctaLabel || $[19] !== opts.ctaStyle) {
    t7 = <Cta label={opts.ctaLabel} style={opts.ctaStyle} />;
    $[18] = opts.ctaLabel;
    $[19] = opts.ctaStyle;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t3 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6 || $[25] !== t7) {
    t8 = <div className={t3}>{t4}{t5}{t6}{t7}</div>;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t2 || $[28] !== t8) {
    t9 = <>{t2}{t8}</>;
    $[27] = t2;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const body = t9;
  if (featured) {
    const t10 = opts.preview ? styles.featuredCardPreview : "";
    let t11;
    if ($[30] !== t10) {
      t11 = [styles.featuredCard, t10].filter(Boolean);
      $[30] = t10;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    const className = t11.join(" ");
    if (opts.preview) {
      let t12;
      if ($[32] !== body || $[33] !== className) {
        t12 = <div className={className} data-entries-grid-featured="">{body}</div>;
        $[32] = body;
        $[33] = className;
        $[34] = t12;
      } else {
        t12 = $[34];
      }
      return t12;
    }
    let t12;
    if ($[35] !== body || $[36] !== className || $[37] !== entry.href) {
      t12 = <a className={className} data-entries-grid-featured="" href={entry.href}>{body}</a>;
      $[35] = body;
      $[36] = className;
      $[37] = entry.href;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    return t12;
  }
  const t10 = overlay ? styles.cardOverlay : "";
  let t11;
  if ($[39] !== t10) {
    t11 = [styles.card, t10].filter(Boolean);
    $[39] = t10;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  const className_0 = t11.join(" ");
  if (opts.preview) {
    let t12;
    if ($[41] !== body || $[42] !== className_0) {
      t12 = <div className={className_0} data-entries-grid-card="">{body}</div>;
      $[41] = body;
      $[42] = className_0;
      $[43] = t12;
    } else {
      t12 = $[43];
    }
    return t12;
  }
  let t12;
  if ($[44] !== body || $[45] !== className_0 || $[46] !== entry.href) {
    t12 = <a className={className_0} data-entries-grid-card="" href={entry.href}>{body}</a>;
    $[44] = body;
    $[45] = className_0;
    $[46] = entry.href;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  return t12;
}
function ListRow(t0) {
  const $ = _c(31);
  const {
    entry,
    opts
  } = t0;
  let t1;
  if ($[0] !== entry || $[1] !== opts.showAuthor || $[2] !== opts.showDate || $[3] !== opts.showReadTime || $[4] !== opts.showTaxonomy) {
    t1 = <CardMeta entry={entry} showAuthor={opts.showAuthor} showDate={opts.showDate} showReadTime={opts.showReadTime} showTaxonomy={opts.showTaxonomy} />;
    $[0] = entry;
    $[1] = opts.showAuthor;
    $[2] = opts.showDate;
    $[3] = opts.showReadTime;
    $[4] = opts.showTaxonomy;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== entry || $[7] !== opts) {
    t2 = opts.showTitle ? <CardTitle className={styles.listTitle} opts={opts} title={entry.title} /> : null;
    $[6] = entry;
    $[7] = opts;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== entry || $[10] !== opts) {
    t3 = opts.showExcerpt && entry.excerpt?.trim() ? <CardExcerpt className={styles.listExcerpt} excerpt={entry.excerpt} opts={opts} /> : null;
    $[9] = entry;
    $[10] = opts;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== opts.ctaLabel || $[13] !== opts.ctaStyle) {
    t4 = <Cta label={opts.ctaLabel} style={opts.ctaStyle} />;
    $[12] = opts.ctaLabel;
    $[13] = opts.ctaStyle;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t1 || $[16] !== t2 || $[17] !== t3 || $[18] !== t4) {
    t5 = <div className={styles.listMain}>{t1}{t2}{t3}{t4}</div>;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== entry || $[21] !== opts.showImage) {
    t6 = opts.showImage ? <EntryMedia className={styles.listThumb} imageAlt={entry.imageAlt || entry.title} imageHeight={entry.imageHeight} imageUrl={entry.imageUrl} imageWidth={entry.imageWidth} /> : null;
    $[20] = entry;
    $[21] = opts.showImage;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t5 || $[24] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const body = t7;
  if (opts.preview) {
    let t8;
    if ($[26] !== body) {
      t8 = <li className={styles.listItem} data-entries-grid-row=""><div className={styles.listLink}>{body}</div></li>;
      $[26] = body;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    return t8;
  }
  let t8;
  if ($[28] !== body || $[29] !== entry.href) {
    t8 = <li className={styles.listItem} data-entries-grid-row=""><a className={styles.listLink} href={entry.href}>{body}</a></li>;
    $[28] = body;
    $[29] = entry.href;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
}

/**
 * Public / canvas card grid for `entries_grid`. Host supplies resolved
 * {@link EntriesGridResolvedItem} rows — no DB access here.
 */
export function EntriesGridPublic({
  items,
  config = {},
  viewport = "desktop",
  className,
  style,
  preview = false,
  emptyMessage = "No published entries to show.",
  autoStack = true
}: EntriesGridPublicProps) {
  const presentation = effectiveEntriesGridPresentation(config, viewport);
  const opts: CardOpts = {
    ...presentation,
    preview
  };
  const limited = items.slice(0, clampEntriesGridLimit(config.limit ?? (items.length > 0 ? items.length : 1)));
  const rootClass = [styles.root, className].filter(Boolean).join(" ");
  const titleSize = presentation.titleFontSize.trim();
  const rootStyle = {
    ...style,
    ["--eg-columns" as string]: String(presentation.columns),
    ["--eg-gap" as string]: presentation.gap,
    ["--eg-aspect" as string]: presentation.imageAspect.replace("/", " / "),
    ...(titleSize ? {
      ["--eg-title-size" as string]: titleSize,
      ["--eg-featured-title-size" as string]: titleSize
    } : {})
  } as CSSProperties;
  if (limited.length === 0) {
    return <div className={rootClass} data-auto-stack={autoStack ? "true" : "false"} data-density={presentation.density} data-entries-grid="" style={rootStyle}>
				<p className={styles.empty}>{emptyMessage}</p>
			</div>;
  }
  const {
    variant
  } = presentation;
  if (variant === "list") {
    return <div className={rootClass} data-auto-stack={autoStack ? "true" : "false"} data-density={presentation.density} data-entries-grid="list" style={rootStyle}>
				<ul className={styles.list}>
					{limited.map(entry => <ListRow entry={entry} key={entry.id} opts={opts} />)}
				</ul>
			</div>;
  }
  if (variant === "featured_grid") {
    const [featured, ...rest] = limited;
    return <div className={rootClass} data-auto-stack={autoStack ? "true" : "false"} data-density={presentation.density} data-entries-grid="featured_grid" style={rootStyle}>
				<div className={styles.featuredWrap}>
					{featured ? <GridCard entry={featured} featured opts={opts} /> : null}
					{rest.length > 0 ? <div className={styles.grid}>
							{rest.map(entry_0 => <GridCard entry={entry_0} key={entry_0.id} opts={opts} />)}
						</div> : null}
				</div>
			</div>;
  }
  if (variant === "masonry") {
    return <div className={rootClass} data-auto-stack={autoStack ? "true" : "false"} data-density={presentation.density} data-entries-grid="masonry" style={rootStyle}>
				<div className={styles.masonry}>
					{limited.map(entry_1 => <div className={styles.masonryItem} key={entry_1.id}>
							<GridCard entry={entry_1} opts={opts} />
						</div>)}
				</div>
			</div>;
  }
  return <div className={rootClass} data-auto-stack={autoStack ? "true" : "false"} data-density={presentation.density} data-entries-grid="grid" style={rootStyle}>
			<div className={styles.grid}>
				{limited.map(entry_2 => <GridCard entry={entry_2} key={entry_2.id} opts={opts} />)}
			</div>
		</div>;
}